import { addDays, format, subDays } from 'date-fns'

export type ReviewStatus = 'not_started' | 'in_progress' | 'complete'
export type HealthIndicator = 'Healthy' | 'At Risk' | 'Critical'

export interface CategoryBase {
  completed: boolean
  score: number // 1-5
  notes: string
  actionItems: string
  followUpDate: string
}

export interface OneOnOneReview extends CategoryBase {
  biWeeklyCompleted: boolean
  prevMonthPerfDiscussed: boolean
  currentMTDPerfDiscussed: boolean
  compDiscussion: boolean
  goalReview: boolean
  motivationDiscussed: boolean
  feedbackProvided: boolean
  feedbackReceived: boolean
  openDiscussionNotes: string
}

export interface GameFilmsReview extends CategoryBase {
  numberCompleted: number
  glowIdentified: string
  growIdentified: string
  managerGlowGrowProvided: boolean
  majorTakeawayAssigned: string
  followUpOnPreviousTakeaway: boolean
}

export interface EITSReview extends CategoryBase {
  minutesObserved: number // Target 300
  coachingMoments: number
  realTimeFeedbackDelivered: boolean
  strengthsObserved: string
  areasNeedingImprovement: string
}

export interface DailyHuddlesReview extends CategoryBase {
  morningHuddlesCompleted: number
  middayHuddlesCompleted: number
  teamEngagementScore: number // 1-10
  roadblocksIdentified: string
  winsCelebrated: string
  keyCommunicationTopics: string
}

export interface TrainingsReview extends CategoryBase {
  numberConducted: number
  topicsCovered: string
  skillGapsAddressed: string
  attendance: number // percentage
  followUpActions: string
}

export interface ProductionReview extends CategoryBase {
  managerComments: string
}

export interface PipelineReview extends CategoryBase {
  priorityListReviewed: boolean
  opportunitiesIdentified: string
  pipelineConcerns: string
  conversionStrategyNotes: string
}

export interface ShadowingReview extends CategoryBase {
  minutesShadowed: number // Target 30
  observations: string
  coachingProvided: string
}

export interface CallBlockReview extends CategoryBase {
  callBlockCompliance: number // percentage
  processingBlockCompliance: number // percentage
  attendanceDuringCallBlocks: boolean
  managerObservations: string
}

export interface AttendanceReview extends CategoryBase {
  attendanceStatus: 'Excellent' | 'Good' | 'Needs Improvement'
  tardies: number
  absences: number
  communicationCompliance: boolean
  scheduleAdherence: number // percentage
  overallAttendanceRating: number // 1-5
}

export interface FullReview {
  oneOnOne: OneOnOneReview
  gameFilms: GameFilmsReview
  eits: EITSReview
  dailyHuddles: DailyHuddlesReview
  trainings: TrainingsReview
  production: ProductionReview
  pipeline: PipelineReview
  shadowing: ShadowingReview
  callBlock: CallBlockReview
  attendance: AttendanceReview
}

export const REVIEW_CATEGORY_KEYS: (keyof FullReview)[] = [
  'oneOnOne',
  'gameFilms',
  'eits',
  'dailyHuddles',
  'trainings',
  'production',
  'pipeline',
  'shadowing',
  'callBlock',
  'attendance',
]

// Resolve a loan officer's review for a given week, falling back to the first
// available week if that week has no data yet.
export function getWeekReview(
  lo: LoanOfficer,
  weekId: string,
): FullReview | undefined {
  return lo.reviews[weekId] || lo.reviews[Object.keys(lo.reviews)[0]]
}

// Derive review status from how many of the 10 categories are completed,
// so status stays in sync as a manager checks sections off.
export function computeReviewStatus(
  review: FullReview | undefined,
): ReviewStatus {
  if (!review) return 'not_started'
  const completedCount = REVIEW_CATEGORY_KEYS.filter(
    (k) => (review[k] as CategoryBase).completed,
  ).length
  if (completedCount === 0) return 'not_started'
  if (completedCount === REVIEW_CATEGORY_KEYS.length) return 'complete'
  return 'in_progress'
}

export interface LoanOfficer {
  id: string
  name: string
  team: string
  managerId: string
  avatar: string
  production: {
    applications: number
    submissions: number
    closings: number
    conversionRate: number // percentage
  }
  reviewStatus: ReviewStatus
  lastReviewDate: string
  accountabilityScore: number // 0-100
  priorMonthScore: number
  healthIndicator: HealthIndicator
  reviews: Record<string, FullReview> // Keyed by week ID e.g. 'W41-2023'
}

import { subWeeks, startOfWeek } from 'date-fns'

const generateWeekId = (weeksAgo: number) => {
  const date = subWeeks(new Date(), weeksAgo)
  const start = startOfWeek(date, { weekStartsOn: 1 })
  return `W${format(start, 'ww-yyyy')}`
}

const currentWeekId = generateWeekId(0)
const prevWeekId1 = generateWeekId(1)
const prevWeekId2 = generateWeekId(2)
const prevWeekId3 = generateWeekId(3)

export interface Rvp {
  id: string
  name: string
  avatar: string
  region: string
  isJoint?: boolean
}

export interface Manager {
  id: string
  name: string
  avatar: string
  team: string
  compliancePercentage: number
  rvpId: string
}

const today = new Date()

const generateBaseCategory = (
  score: number,
  completed: boolean = true,
): CategoryBase => ({
  completed,
  score,
  notes: completed
    ? 'Discussed during our last session. Making good progress.'
    : '',
  actionItems: completed ? 'Continue focusing on key metrics.' : '',
  followUpDate: format(addDays(today, 7), 'yyyy-MM-dd'),
})

export const mockRvps: Rvp[] = [
  {
    id: 'r1',
    name: 'Joseph Dalal',
    avatar: 'https://i.pravatar.cc/150?u=r1',
    region: 'Region 1',
  },
  {
    id: 'r2',
    name: 'Isaac Keer',
    avatar: 'https://i.pravatar.cc/150?u=r2',
    region: 'Region 2',
  },
  {
    id: 'r3',
    name: 'Lou Elia',
    avatar: 'https://i.pravatar.cc/150?u=r3',
    region: 'Region 3',
  },
  {
    id: 'r4',
    name: 'Saif Dalal',
    avatar: 'https://i.pravatar.cc/150?u=r4',
    region: 'Region 4',
  },
  {
    id: 'r5',
    name: 'Marcis Sesi',
    avatar: 'https://i.pravatar.cc/150?u=r5',
    region: 'Region 5',
  },
  {
    id: 'r6',
    name: 'Brandon Istifou & Angelo Tomika',
    avatar: 'https://i.pravatar.cc/150?u=r6',
    region: 'Joint Coverage',
    isJoint: true,
  },
]

export const mockManagers: Manager[] = [
  {
    id: 'm1',
    name: 'Romario George',
    avatar: 'https://i.pravatar.cc/150?u=m1',
    team: 'Team Romario',
    compliancePercentage: 95,
    rvpId: 'r1',
  },
  {
    id: 'm2',
    name: 'Isaiah Khan',
    avatar: 'https://i.pravatar.cc/150?u=m2',
    team: 'Team Isaiah',
    compliancePercentage: 88,
    rvpId: 'r2',
  },
  {
    id: 'm3',
    name: 'Christian Keer',
    avatar: 'https://i.pravatar.cc/150?u=m3',
    team: 'Team Christian',
    compliancePercentage: 92,
    rvpId: 'r2',
  },
  {
    id: 'm4',
    name: 'Haydon Burke',
    avatar: 'https://i.pravatar.cc/150?u=m4',
    team: 'Team Haydon',
    compliancePercentage: 85,
    rvpId: 'r2',
  },
  {
    id: 'm5',
    name: 'Dominique Johnson',
    avatar: 'https://i.pravatar.cc/150?u=m5',
    team: 'Team Dominique',
    compliancePercentage: 90,
    rvpId: 'r2',
  },
  {
    id: 'm6',
    name: 'Salem Rayis',
    avatar: 'https://i.pravatar.cc/150?u=m6',
    team: 'Team Salem',
    compliancePercentage: 80,
    rvpId: 'r3',
  },
  {
    id: 'm7',
    name: 'Andrew Rayis',
    avatar: 'https://i.pravatar.cc/150?u=m7',
    team: 'Team Andrew',
    compliancePercentage: 82,
    rvpId: 'r3',
  },
  {
    id: 'm8',
    name: 'Maryanna Hadeya',
    avatar: 'https://i.pravatar.cc/150?u=m8',
    team: 'Team Maryanna',
    compliancePercentage: 96,
    rvpId: 'r3',
  },
  {
    id: 'm9',
    name: 'Yaseer (Alex) Hakeem',
    avatar: 'https://i.pravatar.cc/150?u=m9',
    team: 'Team Yaseer',
    compliancePercentage: 89,
    rvpId: 'r3',
  },
  {
    id: 'm10',
    name: 'Mena Esho',
    avatar: 'https://i.pravatar.cc/150?u=m10',
    team: 'Team Mena',
    compliancePercentage: 91,
    rvpId: 'r3',
  },
  {
    id: 'm11',
    name: 'Joshua Clement',
    avatar: 'https://i.pravatar.cc/150?u=m11',
    team: 'Team Joshua',
    compliancePercentage: 87,
    rvpId: 'r3',
  },
  {
    id: 'm12',
    name: 'Zaid Dalal',
    avatar: 'https://i.pravatar.cc/150?u=m12',
    team: 'Team Zaid',
    compliancePercentage: 94,
    rvpId: 'r4',
  },
  {
    id: 'm13',
    name: 'Nick Danese',
    avatar: 'https://i.pravatar.cc/150?u=m13',
    team: 'Team Nick',
    compliancePercentage: 83,
    rvpId: 'r5',
  },
  {
    id: 'm14',
    name: 'Calvin Dado',
    avatar: 'https://i.pravatar.cc/150?u=m14',
    team: 'Team Calvin',
    compliancePercentage: 75,
    rvpId: '',
  },
  {
    id: 'm15',
    name: 'Sean O’Keefe',
    avatar: 'https://i.pravatar.cc/150?u=m15',
    team: 'Team Sean',
    compliancePercentage: 78,
    rvpId: '',
  },
]

export const mockLoanOfficers: LoanOfficer[] = [
  // Manager 1 LOs
  {
    id: 'lo1',
    name: 'Emily Davis',
    team: 'Team Romario',
    managerId: 'm1',
    avatar: 'https://i.pravatar.cc/150?u=lo1',
    production: {
      applications: 45,
      submissions: 38,
      closings: 25,
      conversionRate: 65,
    },
    reviewStatus: 'complete',
    lastReviewDate: format(subDays(today, 2), 'yyyy-MM-dd'),
    accountabilityScore: 92,
    priorMonthScore: 88,
    healthIndicator: 'Healthy',
    reviews: {
      [currentWeekId]: {
        oneOnOne: {
          ...generateBaseCategory(5),
          biWeeklyCompleted: true,
          prevMonthPerfDiscussed: true,
          currentMTDPerfDiscussed: true,
          compDiscussion: true,
          goalReview: true,
          motivationDiscussed: true,
          feedbackProvided: true,
          feedbackReceived: true,
          openDiscussionNotes: 'Emily is highly motivated this month.',
        },
        gameFilms: {
          ...generateBaseCategory(4),
          numberCompleted: 4,
          glowIdentified: 'Great tone on discovery calls',
          growIdentified: 'Need to ask more probing questions',
          managerGlowGrowProvided: true,
          majorTakeawayAssigned: 'Use the 3-deep questioning framework',
          followUpOnPreviousTakeaway: true,
        },
        eits: {
          ...generateBaseCategory(5),
          minutesObserved: 320,
          coachingMoments: 8,
          realTimeFeedbackDelivered: true,
          strengthsObserved: 'Excellent rapport building',
          areasNeedingImprovement: 'Closing urgency',
        },
        dailyHuddles: {
          ...generateBaseCategory(5),
          morningHuddlesCompleted: 20,
          middayHuddlesCompleted: 18,
          teamEngagementScore: 9,
          roadblocksIdentified: 'Underwriting delays',
          winsCelebrated: 'Top producer last week',
          keyCommunicationTopics: 'Rate changes',
        },
        trainings: {
          ...generateBaseCategory(4),
          numberConducted: 2,
          topicsCovered: 'Objection Handling',
          skillGapsAddressed: 'Rate objections',
          attendance: 100,
          followUpActions: 'Roleplay next 1:1',
        },
        production: {
          ...generateBaseCategory(5),
          managerComments: 'Crushing goals this month. Keep it up.',
        },
        pipeline: {
          ...generateBaseCategory(4),
          priorityListReviewed: true,
          opportunitiesIdentified: '3 refi candidates',
          pipelineConcerns: 'None currently',
          conversionStrategyNotes: 'Focus on pre-approvals',
        },
        shadowing: {
          ...generateBaseCategory(5),
          minutesShadowed: 45,
          observations: 'Strong process adherence',
          coachingProvided: 'Minor tweaks to CRM usage',
        },
        callBlock: {
          ...generateBaseCategory(4),
          callBlockCompliance: 95,
          processingBlockCompliance: 90,
          attendanceDuringCallBlocks: true,
          managerObservations: 'Very focused during power hours',
        },
        attendance: {
          ...generateBaseCategory(5),
          attendanceStatus: 'Excellent',
          tardies: 0,
          absences: 0,
          communicationCompliance: true,
          scheduleAdherence: 100,
          overallAttendanceRating: 5,
        },
      },
      [prevWeekId1]: {
        oneOnOne: {
          ...generateBaseCategory(4),
          biWeeklyCompleted: true,
          prevMonthPerfDiscussed: true,
          currentMTDPerfDiscussed: true,
          compDiscussion: false,
          goalReview: true,
          motivationDiscussed: true,
          feedbackProvided: true,
          feedbackReceived: true,
          openDiscussionNotes: 'Good week.',
        },
        gameFilms: {
          ...generateBaseCategory(4),
          numberCompleted: 3,
          glowIdentified: 'Tone',
          growIdentified: 'Pacing',
          managerGlowGrowProvided: true,
          majorTakeawayAssigned: 'Pacing',
          followUpOnPreviousTakeaway: true,
        },
        eits: {
          ...generateBaseCategory(4),
          minutesObserved: 280,
          coachingMoments: 6,
          realTimeFeedbackDelivered: true,
          strengthsObserved: 'Rapport',
          areasNeedingImprovement: 'Closing',
        },
        dailyHuddles: {
          ...generateBaseCategory(5),
          morningHuddlesCompleted: 5,
          middayHuddlesCompleted: 5,
          teamEngagementScore: 8,
          roadblocksIdentified: 'None',
          winsCelebrated: 'App taken',
          keyCommunicationTopics: 'Rates',
        },
        trainings: {
          ...generateBaseCategory(4),
          numberConducted: 1,
          topicsCovered: 'Objections',
          skillGapsAddressed: 'Rates',
          attendance: 100,
          followUpActions: 'None',
        },
        production: { ...generateBaseCategory(4), managerComments: 'Solid.' },
        pipeline: {
          ...generateBaseCategory(4),
          priorityListReviewed: true,
          opportunitiesIdentified: 'Refis',
          pipelineConcerns: 'None',
          conversionStrategyNotes: 'Call',
        },
        shadowing: {
          ...generateBaseCategory(4),
          minutesShadowed: 30,
          observations: 'Good',
          coachingProvided: 'None',
        },
        callBlock: {
          ...generateBaseCategory(4),
          callBlockCompliance: 90,
          processingBlockCompliance: 90,
          attendanceDuringCallBlocks: true,
          managerObservations: 'Focused',
        },
        attendance: {
          ...generateBaseCategory(5),
          attendanceStatus: 'Excellent',
          tardies: 0,
          absences: 0,
          communicationCompliance: true,
          scheduleAdherence: 100,
          overallAttendanceRating: 5,
        },
      },
    },
  },
  {
    id: 'lo2',
    name: 'James Wilson',
    team: 'Team Romario',
    managerId: 'm1',
    avatar: 'https://i.pravatar.cc/150?u=lo2',
    production: {
      applications: 30,
      submissions: 20,
      closings: 12,
      conversionRate: 40,
    },
    reviewStatus: 'in_progress',
    lastReviewDate: format(subDays(today, 5), 'yyyy-MM-dd'),
    accountabilityScore: 75,
    priorMonthScore: 80,
    healthIndicator: 'At Risk',
    reviews: {
      [currentWeekId]: {
        oneOnOne: {
          ...generateBaseCategory(3),
          biWeeklyCompleted: true,
          prevMonthPerfDiscussed: true,
          currentMTDPerfDiscussed: true,
          compDiscussion: false,
          goalReview: true,
          motivationDiscussed: false,
          feedbackProvided: true,
          feedbackReceived: false,
          openDiscussionNotes: 'James seems distracted lately.',
        },
        gameFilms: {
          ...generateBaseCategory(3),
          numberCompleted: 2,
          glowIdentified: 'Good product knowledge',
          growIdentified: 'Rushing through disclosures',
          managerGlowGrowProvided: true,
          majorTakeawayAssigned: 'Slow down pace',
          followUpOnPreviousTakeaway: false,
        },
        eits: {
          ...generateBaseCategory(3),
          minutesObserved: 150,
          coachingMoments: 3,
          realTimeFeedbackDelivered: true,
          strengthsObserved: 'Friendly demeanor',
          areasNeedingImprovement: 'Controlling the call',
        },
        dailyHuddles: {
          ...generateBaseCategory(4),
          morningHuddlesCompleted: 15,
          middayHuddlesCompleted: 10,
          teamEngagementScore: 6,
          roadblocksIdentified: 'Lead quality',
          winsCelebrated: 'Saved a tough deal',
          keyCommunicationTopics: 'Pipeline cleanup',
        },
        trainings: {
          ...generateBaseCategory(3),
          numberConducted: 1,
          topicsCovered: 'CRM Basics',
          skillGapsAddressed: 'Task management',
          attendance: 80,
          followUpActions: 'Review task list daily',
        },
        production: {
          ...generateBaseCategory(3),
          managerComments:
            'Conversion is slipping. Need to focus on follow-ups.',
        },
        pipeline: {
          ...generateBaseCategory(3),
          priorityListReviewed: true,
          opportunitiesIdentified: 'Stale leads',
          pipelineConcerns: 'Too many aging files',
          conversionStrategyNotes: 'Implement 10-day drip',
        },
        shadowing: {
          ...generateBaseCategory(2),
          minutesShadowed: 15,
          observations: 'Easily distracted',
          coachingProvided: 'Time blocking techniques',
        },
        callBlock: {
          ...generateBaseCategory(3),
          callBlockCompliance: 70,
          processingBlockCompliance: 60,
          attendanceDuringCallBlocks: false,
          managerObservations: 'Often steps away during blocks',
        },
        attendance: {
          ...generateBaseCategory(4),
          attendanceStatus: 'Good',
          tardies: 2,
          absences: 0,
          communicationCompliance: true,
          scheduleAdherence: 85,
          overallAttendanceRating: 4,
        },
      },
    },
  },
  {
    id: 'lo3',
    name: 'Michael Chang',
    team: 'Team Romario',
    managerId: 'm1',
    avatar: 'https://i.pravatar.cc/150?u=lo3',
    production: {
      applications: 55,
      submissions: 48,
      closings: 35,
      conversionRate: 72,
    },
    reviewStatus: 'complete',
    lastReviewDate: format(subDays(today, 1), 'yyyy-MM-dd'),
    accountabilityScore: 98,
    priorMonthScore: 95,
    healthIndicator: 'Healthy',
    reviews: {
      [currentWeekId]: {
        oneOnOne: {
          ...generateBaseCategory(5),
          biWeeklyCompleted: true,
          prevMonthPerfDiscussed: true,
          currentMTDPerfDiscussed: true,
          compDiscussion: true,
          goalReview: true,
          motivationDiscussed: true,
          feedbackProvided: true,
          feedbackReceived: true,
          openDiscussionNotes: 'Top performer, discussing leadership track.',
        },
        gameFilms: {
          ...generateBaseCategory(5),
          numberCompleted: 5,
          glowIdentified: 'Masterful objection handling',
          growIdentified: 'Delegating to processing sooner',
          managerGlowGrowProvided: true,
          majorTakeawayAssigned: 'Trust the support team',
          followUpOnPreviousTakeaway: true,
        },
        eits: {
          ...generateBaseCategory(5),
          minutesObserved: 350,
          coachingMoments: 10,
          realTimeFeedbackDelivered: true,
          strengthsObserved: 'Closing skills',
          areasNeedingImprovement: 'None major',
        },
        dailyHuddles: {
          ...generateBaseCategory(5),
          morningHuddlesCompleted: 22,
          middayHuddlesCompleted: 22,
          teamEngagementScore: 10,
          roadblocksIdentified: 'None',
          winsCelebrated: 'Record month',
          keyCommunicationTopics: 'Best practices sharing',
        },
        trainings: {
          ...generateBaseCategory(5),
          numberConducted: 3,
          topicsCovered: 'Advanced Sales',
          skillGapsAddressed: 'N/A',
          attendance: 100,
          followUpActions: 'Have Michael lead next team training',
        },
        production: {
          ...generateBaseCategory(5),
          managerComments: 'Exceptional performance.',
        },
        pipeline: {
          ...generateBaseCategory(5),
          priorityListReviewed: true,
          opportunitiesIdentified: 'Jumbo loans',
          pipelineConcerns: 'None',
          conversionStrategyNotes: 'Maintain current cadence',
        },
        shadowing: {
          ...generateBaseCategory(5),
          minutesShadowed: 60,
          observations: 'Flawless execution',
          coachingProvided: 'Peer mentoring advice',
        },
        callBlock: {
          ...generateBaseCategory(5),
          callBlockCompliance: 100,
          processingBlockCompliance: 100,
          attendanceDuringCallBlocks: true,
          managerObservations: 'Sets the standard',
        },
        attendance: {
          ...generateBaseCategory(5),
          attendanceStatus: 'Excellent',
          tardies: 0,
          absences: 0,
          communicationCompliance: true,
          scheduleAdherence: 100,
          overallAttendanceRating: 5,
        },
      },
    },
  },
  // Manager 2 LOs
  {
    id: 'lo4',
    name: 'Samantha Lee',
    team: 'Team Isaiah',
    managerId: 'm2',
    avatar: 'https://i.pravatar.cc/150?u=lo4',
    production: {
      applications: 20,
      submissions: 10,
      closings: 5,
      conversionRate: 25,
    },
    reviewStatus: 'not_started',
    lastReviewDate: format(subDays(today, 35), 'yyyy-MM-dd'),
    accountabilityScore: 55,
    priorMonthScore: 65,
    healthIndicator: 'Critical',
    reviews: {
      [currentWeekId]: {
        oneOnOne: {
          ...generateBaseCategory(2, false),
          biWeeklyCompleted: false,
          prevMonthPerfDiscussed: false,
          currentMTDPerfDiscussed: false,
          compDiscussion: false,
          goalReview: false,
          motivationDiscussed: false,
          feedbackProvided: false,
          feedbackReceived: false,
          openDiscussionNotes: '',
        },
        gameFilms: {
          ...generateBaseCategory(1, false),
          numberCompleted: 0,
          glowIdentified: '',
          growIdentified: '',
          managerGlowGrowProvided: false,
          majorTakeawayAssigned: '',
          followUpOnPreviousTakeaway: false,
        },
        eits: {
          ...generateBaseCategory(2, false),
          minutesObserved: 50,
          coachingMoments: 1,
          realTimeFeedbackDelivered: false,
          strengthsObserved: '',
          areasNeedingImprovement: '',
        },
        dailyHuddles: {
          ...generateBaseCategory(3, false),
          morningHuddlesCompleted: 10,
          middayHuddlesCompleted: 5,
          teamEngagementScore: 4,
          roadblocksIdentified: '',
          winsCelebrated: '',
          keyCommunicationTopics: '',
        },
        trainings: {
          ...generateBaseCategory(2, false),
          numberConducted: 0,
          topicsCovered: '',
          skillGapsAddressed: '',
          attendance: 50,
          followUpActions: '',
        },
        production: {
          ...generateBaseCategory(2, false),
          managerComments: 'Needs immediate intervention.',
        },
        pipeline: {
          ...generateBaseCategory(2, false),
          priorityListReviewed: false,
          opportunitiesIdentified: '',
          pipelineConcerns: '',
          conversionStrategyNotes: '',
        },
        shadowing: {
          ...generateBaseCategory(1, false),
          minutesShadowed: 0,
          observations: '',
          coachingProvided: '',
        },
        callBlock: {
          ...generateBaseCategory(2, false),
          callBlockCompliance: 40,
          processingBlockCompliance: 30,
          attendanceDuringCallBlocks: false,
          managerObservations: '',
        },
        attendance: {
          ...generateBaseCategory(2, false),
          attendanceStatus: 'Needs Improvement',
          tardies: 5,
          absences: 2,
          communicationCompliance: false,
          scheduleAdherence: 60,
          overallAttendanceRating: 2,
        },
      },
    },
  },
  {
    id: 'lo5',
    name: 'Robert Taylor',
    team: 'Team Isaiah',
    managerId: 'm2',
    avatar: 'https://i.pravatar.cc/150?u=lo5',
    production: {
      applications: 38,
      submissions: 30,
      closings: 20,
      conversionRate: 52,
    },
    reviewStatus: 'complete',
    lastReviewDate: format(subDays(today, 10), 'yyyy-MM-dd'),
    accountabilityScore: 85,
    priorMonthScore: 82,
    healthIndicator: 'Healthy',
    reviews: {
      [currentWeekId]: {
        oneOnOne: {
          ...generateBaseCategory(4),
          biWeeklyCompleted: true,
          prevMonthPerfDiscussed: true,
          currentMTDPerfDiscussed: true,
          compDiscussion: false,
          goalReview: true,
          motivationDiscussed: true,
          feedbackProvided: true,
          feedbackReceived: true,
          openDiscussionNotes: 'Solid performer, working on consistency.',
        },
        gameFilms: {
          ...generateBaseCategory(4),
          numberCompleted: 3,
          glowIdentified: 'Good follow-up',
          growIdentified: 'Asking for the business',
          managerGlowGrowProvided: true,
          majorTakeawayAssigned: 'Assume the close',
          followUpOnPreviousTakeaway: true,
        },
        eits: {
          ...generateBaseCategory(4),
          minutesObserved: 280,
          coachingMoments: 5,
          realTimeFeedbackDelivered: true,
          strengthsObserved: 'Persistence',
          areasNeedingImprovement: 'Tone matching',
        },
        dailyHuddles: {
          ...generateBaseCategory(4),
          morningHuddlesCompleted: 18,
          middayHuddlesCompleted: 15,
          teamEngagementScore: 8,
          roadblocksIdentified: 'Appraisal times',
          winsCelebrated: 'Hit monthly app goal',
          keyCommunicationTopics: 'Guidelines',
        },
        trainings: {
          ...generateBaseCategory(4),
          numberConducted: 2,
          topicsCovered: 'VA Loans',
          skillGapsAddressed: 'Product knowledge',
          attendance: 100,
          followUpActions: 'Review VA guidelines',
        },
        production: {
          ...generateBaseCategory(4),
          managerComments: 'Steady growth.',
        },
        pipeline: {
          ...generateBaseCategory(4),
          priorityListReviewed: true,
          opportunitiesIdentified: 'Past clients',
          pipelineConcerns: 'None',
          conversionStrategyNotes: 'Call past database',
        },
        shadowing: {
          ...generateBaseCategory(4),
          minutesShadowed: 30,
          observations: 'Good workflow',
          coachingProvided: 'Shortcut usage',
        },
        callBlock: {
          ...generateBaseCategory(4),
          callBlockCompliance: 85,
          processingBlockCompliance: 80,
          attendanceDuringCallBlocks: true,
          managerObservations: 'Consistent effort',
        },
        attendance: {
          ...generateBaseCategory(4),
          attendanceStatus: 'Good',
          tardies: 1,
          absences: 0,
          communicationCompliance: true,
          scheduleAdherence: 95,
          overallAttendanceRating: 4,
        },
      },
    },
  },
  // Manager 3 LOs
  {
    id: 'lo6',
    name: 'Amanda Martinez',
    team: 'Team Christian',
    managerId: 'm3',
    avatar: 'https://i.pravatar.cc/150?u=lo6',
    production: {
      applications: 42,
      submissions: 35,
      closings: 22,
      conversionRate: 52,
    },
    reviewStatus: 'in_progress',
    lastReviewDate: format(subDays(today, 4), 'yyyy-MM-dd'),
    accountabilityScore: 82,
    priorMonthScore: 78,
    healthIndicator: 'Healthy',
    reviews: {
      [currentWeekId]: {
        oneOnOne: {
          ...generateBaseCategory(4),
          biWeeklyCompleted: true,
          prevMonthPerfDiscussed: true,
          currentMTDPerfDiscussed: true,
          compDiscussion: true,
          goalReview: true,
          motivationDiscussed: true,
          feedbackProvided: true,
          feedbackReceived: true,
          openDiscussionNotes: 'Amanda is stepping up as a leader.',
        },
        gameFilms: {
          ...generateBaseCategory(4),
          numberCompleted: 3,
          glowIdentified: 'Empathy on calls',
          growIdentified: 'Technical knowledge',
          managerGlowGrowProvided: true,
          majorTakeawayAssigned: 'Study new FHA rules',
          followUpOnPreviousTakeaway: true,
        },
        eits: {
          ...generateBaseCategory(4),
          minutesObserved: 250,
          coachingMoments: 6,
          realTimeFeedbackDelivered: true,
          strengthsObserved: 'Active listening',
          areasNeedingImprovement: 'Structuring the loan',
        },
        dailyHuddles: {
          ...generateBaseCategory(4),
          morningHuddlesCompleted: 19,
          middayHuddlesCompleted: 16,
          teamEngagementScore: 8,
          roadblocksIdentified: 'System slowness',
          winsCelebrated: 'Great client review',
          keyCommunicationTopics: 'Process updates',
        },
        trainings: {
          ...generateBaseCategory(4),
          numberConducted: 2,
          topicsCovered: 'FHA Guidelines',
          skillGapsAddressed: 'FHA limits',
          attendance: 100,
          followUpActions: 'Quiz next week',
        },
        production: {
          ...generateBaseCategory(4),
          managerComments: 'Solid month, conversion improving.',
        },
        pipeline: {
          ...generateBaseCategory(4),
          priorityListReviewed: true,
          opportunitiesIdentified: 'FHA streamlines',
          pipelineConcerns: 'None',
          conversionStrategyNotes: 'Target high-rate FHA clients',
        },
        shadowing: {
          ...generateBaseCategory(3),
          minutesShadowed: 20,
          observations: 'Needs better dual monitor use',
          coachingProvided: 'Workspace setup',
        },
        callBlock: {
          ...generateBaseCategory(4),
          callBlockCompliance: 88,
          processingBlockCompliance: 85,
          attendanceDuringCallBlocks: true,
          managerObservations: 'Good energy during blocks',
        },
        attendance: {
          ...generateBaseCategory(5),
          attendanceStatus: 'Excellent',
          tardies: 0,
          absences: 0,
          communicationCompliance: true,
          scheduleAdherence: 100,
          overallAttendanceRating: 5,
        },
      },
    },
  },
  {
    id: 'lo7',
    name: 'Kevin White',
    team: 'Team Christian',
    managerId: 'm3',
    avatar: 'https://i.pravatar.cc/150?u=lo7',
    production: {
      applications: 25,
      submissions: 15,
      closings: 8,
      conversionRate: 32,
    },
    reviewStatus: 'not_started',
    lastReviewDate: format(subDays(today, 40), 'yyyy-MM-dd'),
    accountabilityScore: 62,
    priorMonthScore: 60,
    healthIndicator: 'At Risk',
    reviews: {
      [currentWeekId]: {
        oneOnOne: {
          ...generateBaseCategory(3, false),
          biWeeklyCompleted: false,
          prevMonthPerfDiscussed: false,
          currentMTDPerfDiscussed: false,
          compDiscussion: false,
          goalReview: false,
          motivationDiscussed: false,
          feedbackProvided: false,
          feedbackReceived: false,
          openDiscussionNotes: '',
        },
        gameFilms: {
          ...generateBaseCategory(2, false),
          numberCompleted: 1,
          glowIdentified: '',
          growIdentified: '',
          managerGlowGrowProvided: false,
          majorTakeawayAssigned: '',
          followUpOnPreviousTakeaway: false,
        },
        eits: {
          ...generateBaseCategory(2, false),
          minutesObserved: 100,
          coachingMoments: 2,
          realTimeFeedbackDelivered: false,
          strengthsObserved: '',
          areasNeedingImprovement: '',
        },
        dailyHuddles: {
          ...generateBaseCategory(3, false),
          morningHuddlesCompleted: 12,
          middayHuddlesCompleted: 8,
          teamEngagementScore: 5,
          roadblocksIdentified: '',
          winsCelebrated: '',
          keyCommunicationTopics: '',
        },
        trainings: {
          ...generateBaseCategory(2, false),
          numberConducted: 1,
          topicsCovered: '',
          skillGapsAddressed: '',
          attendance: 70,
          followUpActions: '',
        },
        production: {
          ...generateBaseCategory(2, false),
          managerComments: 'Needs to increase application volume.',
        },
        pipeline: {
          ...generateBaseCategory(3, false),
          priorityListReviewed: false,
          opportunitiesIdentified: '',
          pipelineConcerns: '',
          conversionStrategyNotes: '',
        },
        shadowing: {
          ...generateBaseCategory(2, false),
          minutesShadowed: 10,
          observations: '',
          coachingProvided: '',
        },
        callBlock: {
          ...generateBaseCategory(3, false),
          callBlockCompliance: 60,
          processingBlockCompliance: 50,
          attendanceDuringCallBlocks: false,
          managerObservations: '',
        },
        attendance: {
          ...generateBaseCategory(3, false),
          attendanceStatus: 'Good',
          tardies: 3,
          absences: 1,
          communicationCompliance: true,
          scheduleAdherence: 80,
          overallAttendanceRating: 3,
        },
      },
    },
  },
]
